import {
  COMPUTED_KEY_ORDER,
  QUEST_KEY_ORDER,
  type Computed,
  type Quest,
} from './types'

/**
 * Reproduce Unity's exporter byte-for-byte: keys in the exporter's order,
 * two-space indent, trailing newline.
 *
 * Verified against all 59 shipped files — JSON.stringify(parsed, null, 2) + "\n"
 * round-trips each one exactly.
 */
export function serializeQuest(quest: Quest): string {
  const ordered: Record<string, unknown> = {}
  for (const key of QUEST_KEY_ORDER) {
    if (key === 'computed') ordered[key] = orderComputed(quest.computed)
    // Absent in exports predating the field; write it explicitly so the file is
    // complete rather than carrying the gap forward.
    else if (key === 'disabled') ordered[key] = quest.disabled ?? false
    else ordered[key] = quest[key]
  }
  return JSON.stringify(ordered, null, 2) + '\n'
}

function orderComputed(computed: Computed): Record<string, unknown> {
  const ordered: Record<string, unknown> = {}
  for (const key of COMPUTED_KEY_ORDER) ordered[key] = computed[key]
  return ordered
}

/**
 * Parse a quest file. Deliberately tolerant: a file that fails validation must
 * still open in the editor so it can be fixed, rather than being unreachable.
 * Structural problems are surfaced by validateQuest, not thrown here.
 */
export function parseQuest(text: string): Quest {
  const quest = JSON.parse(text) as Quest
  // Older exports have no `disabled`; treat a missing flag as enabled.
  return { ...quest, disabled: quest.disabled ?? false }
}

export function questPath(id: string): string {
  return `quests/${id}.json`
}

/** GitHub's Contents API takes base64. Quest text is UTF-8 (accented glyphs appear in labels). */
export function toBase64(text: string): string {
  const bytes = new TextEncoder().encode(text)
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary)
}

export function fromBase64(b64: string): string {
  const binary = atob(b64.replace(/\n/g, ''))
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0))
  return new TextDecoder().decode(bytes)
}
